import {
  runCreateWorkspaceWorkflow,
  runEditWorkspaceWorkflow,
  runMoveCanvasWorkflow,
  runMoveWorkspaceToTrashWorkflow,
  runRenameCanvasWorkflow,
  runShareCanvasWorkflow,
  runShareWorkspaceWorkflow,
} from "@/action-dialog";
import { restoreErrorMessage, useRestoreCanvas } from "@/api/archived";
import {
  useCreateCanvas,
  useDeleteCanvas,
  useWorkspaceSidebarData,
} from "@/api/canvas";
import { useSharedWithMeFileRecords } from "@/api/fileRecords";
import {
  useCreateFlowSession,
  useRecentWorkspaceFlowSessions,
} from "@/api/flow_sessions";
import { useSetCanvasFolder, useSetFlowSessionFolder } from "@/api/folders";
import { useCheckBulkPermissions } from "@/api/relations";
import {
  useFavoriteCanvases,
  useFavoriteFileRecords,
} from "@/api/resourceTags";
import { useUserInfo } from "@/api/user";
import { useUsers } from "@/api/users";
import {
  useCreateWorkspace,
  useDeleteWorkspace,
  usePrivateWorkspace,
  useUpdateWorkspace,
} from "@/api/workspaces";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { Accordion } from "@/components/ui/accordion";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { RailAllFavoritesDropTarget } from "@/dnd";
import { ChatsRailDraggableChatRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/ChatsRailDraggableChatRow";
import { useActiveChatFlowSessionId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useActiveChatFlowSessionId";
import { useChatsRailActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useChatsRailActions";
import { FilesRailDraggableFileRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDraggableFileRow";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { RailGroupActionButton } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroupActionButton";
import { RailMultiSelectionMenuProvider } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailSelectionBar } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionBar";
import { RAIL_SELECTION_ROOT_ATTR } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { railZeroStateCopy } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railZeroStateCopy";
import { useRailExpansionGroups } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { useWorkspacesRailMultiSelectionActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/useWorkspacesRailMultiSelectionActions";
import { WorkspacesRailCanvasNode } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasNode";
import { WorkspacesRailWorkspaceRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailWorkspaceRow";
import {
  useActiveFileRecordId,
  useOpenFileRecord,
} from "@/panes/file-record-detail";
import { route } from "@/utils/route";
import {
  FLOW_SESSION_MODES,
  RESOURCE_TAG_FAVORITE,
  type Canvas,
  type OrganizationUser,
  type Workspace,
} from "@cohesive/api-client";
import { Layers, Plus, Star, User, Users } from "lucide-react";
import { useCallback, useMemo, type ReactNode } from "react";
import toast from "react-hot-toast";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

const RAIL_GROUP_IDS = [
  "workspaces-favorites",
  "workspaces-all",
  "workspaces-shared",
] as const;
const RAIL_GROUP_DEFAULT_OPEN = ["workspaces-favorites", "workspaces-all"];

export function WorkspacesRail() {
  const { data, isError } = useWorkspaceSidebarData();
  const { data: userInfo } = useUserInfo();
  const { data: orgUsers } = useUsers();
  const privateWorkspace = usePrivateWorkspace();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeCanvasMatch = useMatch(ROUTES.CANVAS.DETAIL);
  const activeCanvasId = activeCanvasMatch?.params?.canvasId ?? null;

  const newWorkspaceMenu = useRailRowContextMenu();
  const createWorkspace = useCreateWorkspace();
  const [openGroups, setOpenGroups] = useRailExpansionGroups(
    RAIL_GROUP_IDS,
    RAIL_GROUP_DEFAULT_OPEN,
  );
  const {
    selectionCount,
    selectionBarActions,
    leftRailMultiActions,
    clear: clearSelection,
    clearIfOutside,
  } = useWorkspacesRailMultiSelectionActions();

  // Consolidated Favorites: favorite canvases, chats, and files across all
  // workspaces. Canvases/files come from the ?tag=favorite lists; chats from the
  // recent inbox filtered on is_starred.
  const { data: favoriteCanvases = [] } = useFavoriteCanvases();
  const { data: favoriteFiles = [] } = useFavoriteFileRecords();
  const { data: sharedFiles = [] } = useSharedWithMeFileRecords();
  const { data: recentChats = [] } = useRecentWorkspaceFlowSessions(100);
  const activeChatFlowSessionId = useActiveChatFlowSessionId();
  const chatActions = useChatsRailActions();
  const openFileRecord = useOpenFileRecord();
  const activeFileRecordId = useActiveFileRecordId();
  const setCanvasFolder = useSetCanvasFolder();
  const setFlowSessionFolder = useSetFlowSessionFolder();

  const sidebarTeamWorkspaces = useMemo(
    () => (data?.workspaces ?? []).filter((ws) => !ws.is_private),
    [data?.workspaces],
  );

  const workspaceDeleteChecks = useMemo(
    () =>
      sidebarTeamWorkspaces.map((ws) => ({
        resource_object_type: "workspace" as const,
        resource_object_id: ws.id,
        permission: "can_delete" as const,
      })),
    [sidebarTeamWorkspaces],
  );

  const { data: workspacePermissions } = useCheckBulkPermissions({
    checks: workspaceDeleteChecks,
  });

  const deleteWorkspace = useDeleteWorkspace({
    onError: () => toast.error("Failed to delete Workspace"),
  });

  const updateWorkspace = useUpdateWorkspace({
    onError: () => toast.error("Failed to update workspace"),
  });

  const createCanvas = useCreateCanvas({
    onError: () => toast.error("Failed to create canvas"),
  });

  const deleteCanvas = useDeleteCanvas({
    onError: () => toast.error("Failed to delete canvas"),
  });
  const restoreCanvas = useRestoreCanvas({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore canvas")),
  });

  const createFlowSession = useCreateFlowSession({
    onError: () => toast.error("Failed to create chat"),
  });

  const handleNewCanvas = useCallback(
    (workspaceId: string, folderId?: string) => {
      if (!workspaceId || createCanvas.isPending) return;
      createCanvas.mutate(
        { workspaceId },
        {
          onSuccess: (newCanvas) => {
            // Created from a folder's menu → file it into that folder. The
            // canvas exists either way, so a filing failure only means it
            // landed at the workspace root — say so instead of failing silently.
            if (folderId) {
              setCanvasFolder.mutate(
                {
                  canvasId: newCanvas.id,
                  folderId,
                  workspaceId,
                },
                {
                  onError: () =>
                    toast.error(
                      "Canvas created at the workspace root — couldn't file it into the folder.",
                    ),
                },
              );
            }
            navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: newCanvas.id }));
          },
        },
      );
    },
    [createCanvas, navigate, setCanvasFolder],
  );

  const handleNewChat = useCallback(
    (workspaceId: string, folderId?: string) => {
      if (!workspaceId || createFlowSession.isPending) return;
      createFlowSession.mutate(
        {
          mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
          class: "workspace",
          workspace_id: workspaceId,
        },
        {
          onSuccess: (session) => {
            // Created from a folder's menu → file it into that chat folder.
            // The chat exists either way; surface a filing failure.
            if (folderId) {
              setFlowSessionFolder.mutate(
                {
                  flowSessionId: session.id,
                  folderId,
                  workspaceId,
                },
                {
                  onError: () =>
                    toast.error(
                      "Chat created at the workspace root — couldn't file it into the folder.",
                    ),
                },
              );
            }
            navigate(route(ROUTES.CHAT.DETAIL, { flowSessionId: session.id }));
          },
        },
      );
    },
    [createFlowSession, navigate, setFlowSessionFolder],
  );

  const handleRenameCanvas = useCallback((canvas: Canvas) => {
    window.setTimeout(() => void runRenameCanvasWorkflow({ canvas }), 0);
  }, []);

  const handleMoveCanvas = useCallback((canvas: Canvas) => {
    window.setTimeout(() => void runMoveCanvasWorkflow({ canvas }), 0);
  }, []);

  const handleShareCanvas = useCallback((canvas: Canvas) => {
    window.setTimeout(() => void runShareCanvasWorkflow({ canvas }), 0);
  }, []);

  const activeWorkspaceId = pathname.startsWith("/workspaces/")
    ? pathname.split("/")[2]
    : null;

  const handleDeleteCanvas = useCallback(
    (canvas: Canvas) => {
      deleteCanvas.mutate(canvas.id, {
        onSuccess: () => {
          if (activeCanvasId === canvas.id) {
            navigate(ROUTES.CANVAS.ROOT);
          }
          showMovedToTrashToast(() => restoreCanvas.mutate(canvas.id));
        },
      });
    },
    [activeCanvasId, deleteCanvas, navigate, restoreCanvas],
  );

  const moveWorkspaceToTrash = useCallback(
    (workspace: Workspace) =>
      runMoveWorkspaceToTrashWorkflow({
        workspace,
        deleteWorkspace,
        onSuccess: (ws) => {
          if (activeWorkspaceId === ws.id) navigate("/");
        },
      }),
    [activeWorkspaceId, deleteWorkspace, navigate],
  );

  const editWorkspace = useCallback(
    (workspace: Workspace) =>
      runEditWorkspaceWorkflow({ workspace, updateWorkspace }),
    [updateWorkspace],
  );

  // No navigation on success — workspaces aren't destinations anymore; the new
  // workspace appears as a row in the rail.
  const openCreateWorkspace = useCallback(
    () => void runCreateWorkspaceWorkflow({ createWorkspace }),
    [createWorkspace],
  );

  const shareWorkspaceAction = useCallback(
    (workspace: Workspace) => void runShareWorkspaceWorkflow({ workspace }),
    [],
  );

  const currentUserId = userInfo?.user?.id;
  const usersById = useMemo(() => {
    const map = new Map<string, OrganizationUser>();
    for (const u of orgUsers ?? []) map.set(u.id, u);
    return map;
  }, [orgUsers]);

  const sharedCanvases = useMemo(
    () =>
      [...(data?.sharedWithMe ?? [])].sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      ),
    [data?.sharedWithMe],
  );

  const flatRailCanvasPermissionChecks = useMemo(
    () =>
      [...sharedCanvases, ...favoriteCanvases].flatMap((canvas) => [
        {
          resource_object_type: "canvas" as const,
          resource_object_id: canvas.id,
          permission: "can_delete" as const,
        },
        {
          resource_object_type: "canvas" as const,
          resource_object_id: canvas.id,
          permission: "can_manage_assignments" as const,
        },
      ]),
    [sharedCanvases, favoriteCanvases],
  );

  const { data: flatRailCanvasPermissions } = useCheckBulkPermissions({
    checks: flatRailCanvasPermissionChecks,
  });

  const canDeleteFlatRailCanvas = useCallback(
    (canvasId: string) =>
      flatRailCanvasPermissions?.canvas?.[canvasId]?.can_delete ?? false,
    [flatRailCanvasPermissions],
  );

  const canShareFlatRailCanvas = useCallback(
    (canvasId: string) =>
      flatRailCanvasPermissions?.canvas?.[canvasId]?.can_manage_assignments ??
      false,
    [flatRailCanvasPermissions],
  );

  const teamWorkspaceRows = useMemo(
    () =>
      sidebarTeamWorkspaces.map((ws) => (
        <WorkspacesRailWorkspaceRow
          key={ws.id}
          workspace={ws}
          active={activeWorkspaceId === ws.id}
          activeCanvasId={activeCanvasId}
          currentUserId={currentUserId}
          usersById={usersById}
          onRename={() => void editWorkspace(ws)}
          onShare={() => shareWorkspaceAction(ws)}
          onDelete={
            workspacePermissions?.workspace?.[ws.id]?.can_delete
              ? () => void moveWorkspaceToTrash(ws)
              : undefined
          }
          onRenameCanvas={handleRenameCanvas}
          onMoveCanvas={handleMoveCanvas}
          onShareCanvas={handleShareCanvas}
          onDeleteCanvas={handleDeleteCanvas}
          onNewCanvas={(folderId) => handleNewCanvas(ws.id, folderId)}
          onNewChat={(folderId) => handleNewChat(ws.id, folderId)}
          onOpen={() =>
            navigate(route(ROUTES.WORKSPACES.DETAIL, { workspaceId: ws.id }))
          }
        />
      )),
    [
      sidebarTeamWorkspaces,
      handleNewCanvas,
      handleNewChat,
      navigate,
      activeWorkspaceId,
      activeCanvasId,
      currentUserId,
      usersById,
      workspacePermissions,
      moveWorkspaceToTrash,
      editWorkspace,
      handleRenameCanvas,
      handleMoveCanvas,
      handleShareCanvas,
      handleDeleteCanvas,
    ],
  );

  const allWorkspaceItems = useMemo(() => {
    const items: ReactNode[] = [];
    if (privateWorkspace) {
      items.push(
        <WorkspacesRailWorkspaceRow
          key={privateWorkspace.id}
          workspace={privateWorkspace}
          labelOverride="My Workspace"
          icon={User}
          hideWorkspaceMenu
          active={activeWorkspaceId === privateWorkspace.id}
          activeCanvasId={activeCanvasId}
          currentUserId={currentUserId}
          usersById={usersById}
          onRename={() => {}}
          onShare={() => {}}
          onRenameCanvas={handleRenameCanvas}
          onMoveCanvas={handleMoveCanvas}
          onShareCanvas={handleShareCanvas}
          onDeleteCanvas={handleDeleteCanvas}
          onNewCanvas={(folderId) =>
            handleNewCanvas(privateWorkspace.id, folderId)
          }
          onNewChat={(folderId) => handleNewChat(privateWorkspace.id, folderId)}
          onOpen={() =>
            navigate(
              route(ROUTES.WORKSPACES.DETAIL, {
                workspaceId: privateWorkspace.id,
              }),
            )
          }
          newCanvasDisabled={!privateWorkspace.id || createCanvas.isPending}
        />,
      );
    }
    items.push(...teamWorkspaceRows);
    return items;
  }, [
    privateWorkspace,
    teamWorkspaceRows,
    activeWorkspaceId,
    activeCanvasId,
    currentUserId,
    usersById,
    handleRenameCanvas,
    handleMoveCanvas,
    handleShareCanvas,
    handleDeleteCanvas,
    handleNewCanvas,
    handleNewChat,
    navigate,
    createCanvas.isPending,
  ]);

  if (isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">
          Couldn&apos;t load workspaces.
        </p>
      </div>
    );
  }

  // Shared canvases expand to their files, same as canvases under a workspace.
  const sharedCanvasRows = sharedCanvases.map((canvas) => (
    <WorkspacesRailCanvasNode
      key={canvas.id}
      canvas={canvas}
      dragNamespace="shared"
      active={activeCanvasId === canvas.id}
      onRename={handleRenameCanvas}
      onMove={handleMoveCanvas}
      onShare={
        canShareFlatRailCanvas(canvas.id) ? handleShareCanvas : undefined
      }
      onDelete={
        canDeleteFlatRailCanvas(canvas.id) ? handleDeleteCanvas : undefined
      }
    />
  ));

  // Files explicitly shared with me (direct SpiceDB grants), independent of any
  // workspace/canvas membership. Draggable like every other file row — carrying
  // their own workspace_id; the backend enforces edit permission on drop. No
  // per-row menu actions: rename/move/delete are parent-scoped.
  const sharedFileRows = sharedFiles.map((record) => (
    <FilesRailDraggableFileRow
      key={`shared-file-${record.id}`}
      record={record}
      workspaceId={record.workspace_id ?? ""}
      dragNamespace="files-rail-shared"
      active={activeFileRecordId === record.id}
      onOpen={() => openFileRecord(record.id)}
      // Dropping a shared file into a workspace copies it (the recipient can't
      // relocate it out of the owner's scope) — never moves.
      sharedSource
    />
  ));

  // Chats can't be shared, so the mixed section is canvases + files only.
  const sharedRows = [...sharedCanvasRows, ...sharedFileRows];

  // Filter on tags (not just the fetched set) so an optimistic unstar drops the
  // row immediately, before the ?tag=favorite refetch lands — matching the
  // skills/connectors rails. `section="favorites"` keeps the highlight on the
  // row you clicked instead of also lighting up the canonical copy. Starred
  // canvases expand to their files, same as everywhere else.
  const favoriteCanvasRows = favoriteCanvases
    .filter((canvas) => canvas.tags?.includes(RESOURCE_TAG_FAVORITE))
    .map((canvas) => (
      <WorkspacesRailCanvasNode
        key={canvas.id}
        canvas={canvas}
        dragNamespace="favorites"
        section="favorites"
        active={activeCanvasId === canvas.id}
        onRename={handleRenameCanvas}
        onMove={handleMoveCanvas}
        onShare={
          canShareFlatRailCanvas(canvas.id) ? handleShareCanvas : undefined
        }
        onDelete={
          canDeleteFlatRailCanvas(canvas.id) ? handleDeleteCanvas : undefined
        }
      />
    ));

  // Chat favorites — recent inbox filtered on is_starred (drops immediately on
  // optimistic unstar, matching the canvas/file favorite lists).
  const favoriteChatRows = recentChats
    .filter((item) => item.is_starred)
    .map((item) => (
      <ChatsRailDraggableChatRow
        key={`fav-chat-${item.id}`}
        item={item}
        dragNamespace="favorites"
        section="favorites"
        active={activeChatFlowSessionId === item.flow_session_id}
        showActionsMenu
        onOpen={chatActions.onOpen}
        onToggleStar={chatActions.onToggleStar}
        onRename={chatActions.onRename}
        onDelete={chatActions.onDelete}
      />
    ));

  // File favorites — the ?tag=favorite workspace file list.
  const favoriteFileRows = favoriteFiles
    .filter((record) => record.tags?.includes(RESOURCE_TAG_FAVORITE))
    .map((record) => (
      <FilesRailDraggableFileRow
        key={`fav-file-${record.id}`}
        record={record}
        workspaceId={record.workspace_id ?? ""}
        dragNamespace="favorites"
        section="favorites"
        active={activeFileRecordId === record.id}
        onOpen={() => openFileRecord(record.id)}
      />
    ));

  const favoriteRows = [
    ...favoriteCanvasRows,
    ...favoriteChatRows,
    ...favoriteFileRows,
  ];

  return (
    <RailMultiSelectionMenuProvider actions={leftRailMultiActions}>
      {/* Positioning context for the floating selection bar, which overlays
          the top of the scroll area instead of pushing the rail down. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <RailSelectionBar
          count={selectionCount}
          registryActions={selectionBarActions}
          onClose={clearSelection}
        />
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-2"
          {...{ [RAIL_SELECTION_ROOT_ATTR]: "" }}
          onClick={clearIfOutside}
        >
          <Accordion
            type="multiple"
            // Starred + Workspaces are expanded by default; Shared starts
            // collapsed. The value persists (with the rest of the rail's
            // expansion state) so the rail comes back exactly as you left it.
            value={openGroups}
            onValueChange={setOpenGroups}
            className="flex flex-col"
          >
            <RailAllFavoritesDropTarget>
              <RailGroup
                groupId="workspaces-favorites"
                label="Starred"
                icon={Star}
                items={favoriteRows}
                emptyState={
                  <RailZeroState
                    icon={Star}
                    accent="rust"
                    variant="passive"
                    title="Pin what matters."
                    description="Star a canvas, chat, or file and it stays here."
                  />
                }
              />
            </RailAllFavoritesDropTarget>

            <RailGroup
              groupId="workspaces-all"
              label="Workspaces"
              icon={Layers}
              noCap
              onContextMenu={newWorkspaceMenu.onContextMenu}
              rightSlot={
                <RailGroupActionButton
                  label="New Workspace"
                  onClick={() =>
                    window.setTimeout(() => openCreateWorkspace(), 0)
                  }
                />
              }
              items={allWorkspaceItems}
              emptyState={
                <RailZeroState
                  icon={Layers}
                  accent="rust"
                  title={railZeroStateCopy.workspaces.teams.title}
                  description={railZeroStateCopy.workspaces.teams.description}
                  action={{
                    label: railZeroStateCopy.workspaces.teams.cta,
                    onClick: () =>
                      window.setTimeout(() => openCreateWorkspace(), 0),
                  }}
                />
              }
            />

            <RailGroup
              groupId="workspaces-shared"
              label="Shared with me"
              icon={Users}
              items={sharedRows}
              emptyState={
                <RailZeroState
                  icon={Users}
                  accent="rust"
                  variant="passive"
                  title={railZeroStateCopy.workspaces.shared.title}
                  description={railZeroStateCopy.workspaces.shared.description}
                />
              }
            />
          </Accordion>
        </div>
      </div>

      <RailRowMenu
        {...railRowMenuProps(newWorkspaceMenu)}
        kebab={false}
        label="Workspaces"
      >
        <DropdownMenuItem
          onClick={() => window.setTimeout(() => openCreateWorkspace(), 0)}
        >
          <Plus />
          New Workspace
        </DropdownMenuItem>
      </RailRowMenu>
    </RailMultiSelectionMenuProvider>
  );
}
